import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import styles from './Navigation.module.css';
import logo from '../../assets/logo_final.png';
import FeedbackModal from '../Modal/FeedbackModal';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import { isAdminUser } from '../../config/environment';

const Navigation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const toolsDropdownRef = useRef<HTMLLIElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/home'); // Redirect to public page
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setIsToolsDropdownOpen(false); // Close tools dropdown
    setIsMobileMenuOpen(false); // Close mobile menu
  };

  const toggleToolsDropdown = () => {
    setIsToolsDropdownOpen(!isToolsDropdownOpen);
    setIsUserDropdownOpen(false); // Close user dropdown
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsUserDropdownOpen(false);
    setIsToolsDropdownOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target as Node)) {
        setIsToolsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate avatar initials from email
  const getAvatarInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <nav className={styles.nav}>
      <div className={styles.logo}>
        <Link to={isAuthenticated ? "/home" : "/"} className={styles.logoLink}>
          <img 
            src={logo} 
            alt="Kingdom Studio Pro" 
            className={styles.logoImage}
          />
          <span className={styles.logoText}>
            Kingdom Studio
            <span className={styles.betaLabel}>V1</span>
          </span>
        </Link>
      </div>

      {/* Mobile Burger Menu Button */}
      <button 
        className={styles.mobileMenuButton}
        onClick={toggleMobileMenu}
        aria-label="Toggle mobile menu"
      >
        <span className={`${styles.burgerLine} ${isMobileMenuOpen ? styles.open : ''}`}></span>
        <span className={`${styles.burgerLine} ${isMobileMenuOpen ? styles.open : ''}`}></span>
        <span className={`${styles.burgerLine} ${isMobileMenuOpen ? styles.open : ''}`}></span>
      </button>

      {/* Desktop Navigation */}
      <ul className={`${styles.navLinks} ${styles.desktop}`}>
        <li  style={{ marginTop: '5px' }}>
          <Link to={isAuthenticated ? "/home" : "/"} className={styles.link}>{t('nav.home')}</Link>
        </li>
        {isAuthenticated && (
          <li className={styles.toolsDropdownContainer} ref={toolsDropdownRef}>
            <button 
              className={`${styles.link} ${styles.toolsButton}`}
              onClick={toggleToolsDropdown}
              aria-label="Tools menu"
              style={{ marginTop: '-3px' }}
            >
              {t('nav.tools')}
              <span className={`${styles.dropdownArrow} ${isToolsDropdownOpen ? styles.open : ''}`}>▼</span>
            </button>
            
            {isToolsDropdownOpen && (
              <div className={styles.toolsDropdown}>
                {/* CREATIONS CATEGORY */}
                <div className={styles.toolCategory}>
                  <div className={styles.categoryHeader}>
                    <span className={styles.categoryIcon}>🎬</span>
                    <span className={styles.categoryTitle}>{t('nav.creations')}</span>
                  </div>
                  <div className={styles.toolsGrid}>
                    <Link 
                      to="/create-content" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>✨</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiContentStudio.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiContentStudio.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/script-generator" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>📝</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiScriptGenerator.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiScriptGenerator.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/prompt-generator" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>💿</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiPromptGenerator.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiPromptGenerator.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/audio-generation" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>🎵</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiAudioGenerator.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiAudioGenerator.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/sound-generation" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>🎼</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiSoundtrackGenerator.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiSoundtrackGenerator.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/thumbnail-landing" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>🖼️</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiThumbnailGenerator.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiThumbnailGenerator.description')}</span>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* EDIT CATEGORY */}
                <div className={styles.toolCategory}>
                  <div className={styles.categoryHeader}>
                    <span className={styles.categoryIcon}>✂️</span>
                    <span className={styles.categoryTitle}>{t('nav.edit')}</span>
                  </div>
                  <div className={styles.toolsGrid}>
                    <Link 
                      to="/ai-video-editor" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>🎞️</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.aiVideoEditor.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.aiVideoEditor.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/Caption-LandingPage" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>🎬</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.generateCaption.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.generateCaption.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/title-landing" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>📋</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.generateTitlesDescriptions.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.generateTitlesDescriptions.description')}</span>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/social-media" 
                      className={styles.toolItem}
                      onClick={() => setIsToolsDropdownOpen(false)}
                    >
                      <span className={styles.toolIcon}>📱</span>
                      <div>
                        <span className={styles.toolName}>{t('tools.shareOnSocialMedia.name')}</span>
                        <span className={styles.toolDesc}>{t('tools.shareOnSocialMedia.description')}</span>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </li>
        )}
        <li style={{ marginTop: '5px' }}>
          <Link to="/features" className={styles.link}>{t('nav.features')}</Link>
        </li>
        <li  style={{ marginTop: '5px' }}>
          <Link to="/pricing" className={styles.link}>{t('nav.pricing')}</Link>
        </li>
        <li  style={{ marginTop: '5px' }}>
          <Link to="/about" className={styles.link}>{t('nav.about')}</Link>
        </li>
        <li  style={{ marginTop: '5px' }}>
          <a href="mailto:info@kingdomstudio.pro" className={styles.link}>{t('nav.contact')}</a>
        </li>
        {isAdminUser(user) && (
          <li>
            <button 
              className={styles.feedbackButton}
              onClick={() => setIsFeedbackModalOpen(true)}
              aria-label="Give feedback"
            >
              💭 {t('nav.feedback')}
            </button>
          </li>
        )}
      </ul>

      {/* Desktop User Actions */}
      <div className={`${styles.navActions} ${styles.desktop}`} style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{marginRight: '1rem'}} >
        <LanguageSwitcher />
        </div>
        {isAuthenticated ? (
          <div className={styles.userMenu} ref={userDropdownRef}>
            <button 
              className={styles.avatarButton}
              onClick={toggleUserDropdown}
              aria-label="User menu"
            >
              <div className={styles.avatar}>
                {getAvatarInitials(user?.email || 'U')}
              </div>
            </button>
            
            {isUserDropdownOpen && (
              <div className={styles.dropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <span className={styles.userEmail}>{user?.email || ''}</span>
                    <div className={styles.planInfo}>
                      <span className={styles.planBadge}>
                        {user?.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : 'Free'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className={styles.dropdownContent}>
                  <button 
                    className={styles.dropdownButton}
                    onClick={() => {
                      navigate('/pricing');
                      setIsUserDropdownOpen(false);
                    }}
                  >
                    <span className={styles.buttonIcon}>⚡</span>
                    {t('nav.upgradePlan')}
                  </button>
                  
                  <button 
                    className={styles.dropdownButton}
                    onClick={() => {
                      navigate('/usage');
                      setIsUserDropdownOpen(false);
                    }}
                  >
                    <span className={styles.buttonIcon}>📊</span>
                    {t('nav.usage')}
                  </button>
                  
                  <button 
                    className={styles.dropdownButton}
                    onClick={() => {
                      navigate('/settings');
                      setIsUserDropdownOpen(false);
                    }}
                  >
                    <span className={styles.buttonIcon}>⚙️</span>
                    {t('nav.settings')}
                  </button>
                  
                  <div className={styles.dropdownDivider}></div>
                  
                  <button 
                    className={`${styles.dropdownButton} ${styles.logoutBtn}`}
                    onClick={handleLogout}
                  >
                    <span className={styles.buttonIcon}>🚪</span>
                    {t('nav.signOut')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.authButtons}>
            {/* <button className={styles.demoButton} onClick={() => navigate('/create-content')}>
              {t('nav.tryFreeDemo')}
            </button> */}
            <button className={styles.signupButton} onClick={() => navigate('/login-page?tab=signup')}>
              {t('nav.signup')}
            </button>
            <button className={styles.loginButton} onClick={() => navigate('/login-page')}>
              {t('nav.login')}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      <div 
        className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`}
        ref={mobileMenuRef}
      >
        {/* Close Button */}
        <button 
          className={styles.mobileCloseButton}
          onClick={closeMobileMenu}
          aria-label="Close mobile menu"
        >
          <span className={styles.closeIcon}>×</span>
        </button>
        
        <div className={styles.mobileMenuContent}>
          <Link 
            to={isAuthenticated ? "/home" : "/"} 
            className={styles.mobileMenuItem}
            onClick={closeMobileMenu}
          >
            {t('nav.home')}
          </Link>
          
          {isAuthenticated && (
            <>
              {/* CREATIONS CATEGORY - Mobile */}
              <div className={styles.mobileToolsSection}>
                <span className={styles.mobileMenuTitle}>
                  <span className={styles.mobileCategoryIcon}>🎬</span> {t('nav.creations')}
                </span>
                <Link to="/create-content" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  ✨ {t('tools.aiContentStudio.name')}
                </Link>
                <Link to="/script-generator" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  📝 {t('tools.aiScriptGenerator.name')}
                </Link>
                <Link to="/prompt-generator" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  💿 {t('tools.aiPromptGenerator.name')}
                </Link>
                <Link to="/audio-generation" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  🎵 {t('tools.aiAudioGenerator.name')}
                </Link>
                <Link to="/sound-generation" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  🎼 {t('tools.aiSoundtrackGenerator.name')}
                </Link>
                <Link to="/thumbnail-landing" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  🖼️ {t('tools.aiThumbnailGenerator.name')}
                </Link>
              </div>

              {/* EDIT CATEGORY - Mobile */}
              <div className={styles.mobileToolsSection}>
                <span className={styles.mobileMenuTitle}>
                  <span className={styles.mobileCategoryIcon}>✂️</span> {t('nav.edit')}
                </span>
                <Link to="/ai-video-editor" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  🎞️ {t('tools.aiVideoEditor.name')}
                </Link>
                <Link to="/Caption-LandingPage" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  🎬 {t('tools.generateCaption.name')}
                </Link>
                <Link to="/title-landing" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  📋 {t('tools.generateTitlesDescriptions.name')}
                </Link>
                <Link to="/social-media" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
                  📱 {t('tools.shareOnSocialMedia.name')}
                </Link>
              </div>
            </>
          )}
          
          <Link to="/features" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
            {t('nav.features')}
          </Link>
          <Link to="/pricing" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
            {t('nav.pricing')}
          </Link>
          <Link to="/about" className={styles.mobileMenuItem} onClick={closeMobileMenu}>
            {t('nav.about')}
          </Link>
          <a href="mailto:info@kingdomstudio.pro" className={styles.mobileMenuItem}>
            {t('nav.contact')}
          </a>
          
          <div className={styles.mobileLanguageSwitcher}>
            <LanguageSwitcher />
          </div>
          
          <button 
            className={styles.mobileMenuItem}
            onClick={() => {
              setIsFeedbackModalOpen(true);
              closeMobileMenu();
            }}
          >
            💭 {t('nav.feedback')}
          </button>

          {isAuthenticated ? (
            <div className={styles.mobileUserSection}>
              <div className={styles.mobileUserInfo}>
                <span className={styles.mobileUserEmail}>{user?.email}</span>
                <span className={styles.mobilePlanBadge}>
                  {user?.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : 'Free'}
                </span>
              </div>
              
              <button 
                className={styles.mobileMenuItem}
                onClick={() => {
                  navigate('/pricing');
                  closeMobileMenu();
                }}
              >
                ⚡ {t('nav.upgradePlan')}
              </button>
              
              <button 
                className={styles.mobileMenuItem}
                onClick={() => {
                  navigate('/usage');
                  closeMobileMenu();
                }}
              >
                📊 {t('nav.usage')}
              </button>
              
              <button 
                className={styles.mobileMenuItem}
                onClick={() => {
                  navigate('/settings');
                  closeMobileMenu();
                }}
              >
                ⚙️ {t('nav.settings')}
              </button>
              
              <button 
                className={`${styles.mobileMenuItem} ${styles.logoutButton}`}
                onClick={handleLogout}
              >
                🚪 {t('nav.signOut')}
              </button>
            </div>
          ) : (
            <div className={styles.mobileAuthSection}>
              <button 
                className={styles.mobileSignupButton} 
                onClick={() => {
                  navigate('/login-page?tab=signup');
                  closeMobileMenu();
                }}
              >
                {t('nav.signup')}
              </button>
              <button 
                className={styles.mobileLoginButton} 
                onClick={() => {
                  navigate('/login-page');
                  closeMobileMenu();
                }}
              >
                {t('nav.login')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </nav>
  );
};

export default Navigation;
